import { FormattedMessage } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { queryRelations } from '@plone/volto/actions/relations/relations';
import { getContent } from '@plone/volto/actions/content/content';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect } from 'react';
import { expandToBackendURL } from '@plone/volto/helpers/Url/Url';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import { loadTaxonomy } from '../../actions';

// react-intl messages for personData
export const messages = defineMessages({
  'persons.honorarytitle': {
    id: 'persons.honorarytitle',
    defaultMessage: 'Ehrentitel',
  },
  'persons.honorarytitle.plural': {
    id: 'persons.honorarytitle.plural',
    defaultMessage:
      '{count, plural, one {Honorary Title} other {Honorary Titles}}',
  },
  'persons.employment-type': {
    id: 'persons.employment-type',
    defaultMessage: 'Beschäftigungsverhältnis',
  },
  'persons.function': { id: 'persons.function', defaultMessage: 'Funktion' },
  'persons.membership': {
    id: 'persons.membership',
    defaultMessage: 'Akademiemitgliedschaft',
  },
  'persons.membership-access': {
    id: 'persons.membership-access',
    defaultMessage: 'Zuwahl',
  },
  'persons.membership-withdrawal': {
    id: 'persons.membership-withdrawal',
    defaultMessage: 'End of membership',
  },
  'persons.membership-type': {
    id: 'persons.membership-type',
    defaultMessage: 'Art der Mitgliedschaft',
  },
  'persons.classification': {
    id: 'persons.classification',
    defaultMessage: 'Klasse',
  },
  'persons.projects': { id: 'persons.projects', defaultMessage: 'Projekte' },
  'persons.websites': {
    id: 'persons.websites',
    defaultMessage: '{count, plural, one {Webpage} other {Webpages}}',
  },
  'persons.biography': {
    id: 'persons.biography',
    defaultMessage: 'Biografie / Sonstige Angaben',
  },
  'persons.dob': { id: 'persons.dob', defaultMessage: 'Geburtsdatum' },
  'persons.pob': { id: 'persons.pob', defaultMessage: 'Geburtsort' },
  'persons.dod': { id: 'persons.dod', defaultMessage: 'Sterbedatum' },
  'persons.pod': { id: 'persons.pod', defaultMessage: 'Sterbeort' },
  'persons.obit': { id: 'persons.obit', defaultMessage: 'Nachruf' },
  'persons.research': {
    id: 'persons.research',
    defaultMessage: 'Forschungsgebiete',
  },
  a604eixtb4: {
    id: 'person-project',
    defaultMessage: '{count, plural, one {Project} other {Projects}}',
  },
  b207nuqiq2: {
    id: 'person-kommission',
    defaultMessage: '{count, plural, one {Commission} other {Commissions}}',
  },
  c633poiit1: {
    id: 'person-referat',
    defaultMessage: '{count, plural, one {Department} other {Departments}}',
  },
  'listing.readmore.profileOf': {
    id: 'listing.readmore.profileOf',
    defaultMessage: 'to profile of {name}',
  },
});

// mocked helper functions --> TODO: Replace with actual implementations
const checkPermission = (perm, context) => false;

const PersonMetadata = (props) => {
  const { content, location, metadata, data, editable } = props;
  const showBioHeadline = data?.showBioHeadline || false;
  const isEditMode = editable;
  const personData = metadata || content || {};
  const dispatch = useDispatch();
  const intl = useIntl();
  const toLocalizedDate = (date) =>
    new Date(date).toLocaleDateString(intl.locale);
  const path = getBaseUrl(location?.pathname || '');
  const hidden = personData?.hidden_fields || [];
  const parentpath = personData?.parent?.['@id'] || '';
  const person_context = useSelector(
    (state) => state.content.subrequests[parentpath]?.data,
  );
  const hide_details_on_alumni = person_context?.hide_details_on_alumni;
  const hide_projects = person_context?.hide_projects;
  const alumni = personData?.former;
  const user_logged_in = useSelector((state) => state.userSession?.token);
  const canModify = user_logged_in
    ? true
    : checkPermission('Modify portal content', personData);
  personData.text = null;
  const taxonomies = useSelector((state) => state.taxonomies);
  useEffect(() => {
    const taxos = ['collective.taxonomy.project_type'];
    taxos.forEach((name) => {
      if (!taxonomies?.[name]?.loaded) {
        dispatch(loadTaxonomy(name));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const taxo_projecttype = useSelector(
    (state) => state.taxonomies?.['collective.taxonomy.project_type'],
  );
  const language = useSelector((state) => state.intl.locale);
  const getTaxonomyTitle = (token, taxonomy) => {
    if (taxonomy && taxonomy.data?.tree) {
      const term = taxonomy.data.tree.find((term) => term.key === token);
      return term ? term.translations?.[language] ?? term.title : token;
    }
  };
  const related_projectypes = taxo_projecttype?.data?.tree?.map((t) => t.key);
  // const personType = personData?.['@type'];
  const isPropertyHidden = (propname) => {
    return (
      (hide_details_on_alumni && alumni) ||
      hidden.map((a) => a.token).includes(propname)
    );
  };

  const hasBio =
    ((!isPropertyHidden('dob') || canModify) &&
      (personData?.dob || personData?.dob_alternative)) ||
    ((!isPropertyHidden('dod') || canModify) &&
      (personData?.dod || personData?.dod_alternative)) ||
    ((!isPropertyHidden('pob') || canModify) &&
      (personData?.pob || personData?.pob_alternative)) ||
    ((!isPropertyHidden('pod') || canModify) &&
      (personData?.pod || personData?.pod_alternative));
  const myrelations = useSelector(
    (state) => state.relations.subrequests[path]?.data,
  );

  useEffect(() => {
    dispatch(queryRelations(null, false, path, null, [path]));
    dispatch(getContent(expandToBackendURL(parentpath), false, parentpath));
  }, [dispatch, path]);

  let links = {};
  if (myrelations) {
    Object.keys(myrelations).forEach((relationtype) => {
      links[relationtype] = {};
      myrelations[relationtype].items.forEach((item) => {
        links[relationtype][item.source.UID] = item.source;
      });
    });
  }

  const groupedProjects = (myrelations?.persons?.items || []).reduce(
    (groups, item) => {
      let typeslist = item?.source.taxonomy_project_type || [];
      if (item.source?.['@type'] === 'saw.persons.workgroup') {
        typeslist = item?.source?.getProjectList
          ? item.source.getProjectList[0].type || []
          : [];
      }
      const types = typeslist.filter((x) =>
        new Set(related_projectypes || []).has(x),
      );
      for (const type of types) {
        if (!groups[type]) {
          groups[type] = [];
        }
        groups[type].push(item.source);
      }
      return groups;
    },
    {},
  );
  return (
    <>
      {/* Honorary Titles */}
      {personData?.honorary_title && (
        <div className="person-honorary-titles">
          <h2>
            {intl.formatMessage(messages['persons.honorarytitle.plural'], {
              count: personData.honorary_title.length,
            })}
          </h2>
          <div className="person-honorary-titles-details">
            {personData?.honorary_title.join(', ')}
          </div>
        </div>
      )}

      {personData?.taxonomy_classification && (
        <div className="person-membership">
          <h2>
            <FormattedMessage id="persons.membership" />
          </h2>
          <div className="person-membership-details">
            {personData?.access &&
              (!hidden.map((a) => a.token).includes('access') || canModify) && (
                <div
                  className={
                    hidden.map((a) => a.token).includes('access')
                      ? 'person-access state-private'
                      : 'person-access'
                  }
                >
                  <span>
                    <FormattedMessage id="persons.membership-access" />:{' '}
                  </span>
                  <span>{toLocalizedDate(personData?.access)}</span>
                </div>
              )}
            {personData?.withdrawal &&
              (!hidden.map((a) => a.token).includes('withdrawal') ||
                canModify) && (
                <div
                  className={
                    hidden.map((a) => a.token).includes('withdrawal')
                      ? 'person-withdrawal state-private'
                      : 'person-withdrawal'
                  }
                >
                  <span>
                    <FormattedMessage id="persons.membership-withdrawal" />:{' '}
                  </span>
                  <span>{toLocalizedDate(personData?.withdrawal)}</span>
                </div>
              )}

            {personData?.taxonomy_membership_type && (
              <>
                <div className="membership-type">
                  <span>
                    <FormattedMessage id="persons.membership-type" />:{' '}
                  </span>
                  <span>
                    {personData?.taxonomy_membership_type
                      ?.map((group) => {
                        return group.title;
                      })
                      .join(', ')}
                  </span>
                </div>
              </>
            )}
            {personData?.taxonomy_classification && (
              <>
                <div className="classification">
                  <span>
                    <FormattedMessage id="persons.classification" />:{' '}
                  </span>
                  <span>
                    {personData?.taxonomy_classification
                      ?.map((classifications) => {
                        return classifications.title;
                      })
                      .join(', ')}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* {personData?.taxonomy_employment_type &&
        personData?.taxonomy_employment_type.length > 0 && (
          <div className="person-employment-type">
            <h2>
              <FormattedMessage id="persons.employment-type" />
            </h2>
            <div className="person-employment-type-details">
              {personData?.taxonomy_employment_type.map((empl_func, index) => (
                <div key={index} className="label">
                  <span>
                    <FormattedMessage id="persons.function" />:
                  </span>{' '}
                  <span>{empl_func.title}</span>
                </div>
              ))}
            </div>
          </div>
        )} */}

      {personData?.websites && personData?.websites.length > 0 && (
        <div className="person-websites">
          <h2>
            {intl.formatMessage(messages['persons.websites'], {
              count: personData.websites.length,
            })}
          </h2>

          {personData?.websites.map((website, index) => {
            const parts = website.split('|');
            return (
              <div key={index}>
                <a
                  href={parts[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="external"
                >
                  {parts.length > 1 && parts[1] ? parts[1] : parts[0]}
                </a>
              </div>
            );
          })}
        </div>
      )}

      {!hide_projects && Object.keys(groupedProjects).length > 0 && (
        <div className="person-projects">
          {Object.keys(groupedProjects)
            .sort(
              (a, b) =>
                related_projectypes.indexOf(a) - related_projectypes.indexOf(b),
            )
            .map((groupKey, idx) => {
              const groupname =
                getTaxonomyTitle(groupKey, taxo_projecttype) || 'Projekte';
              return (
                <div key={idx} className="project-group">
                  <h2 className="project-group-title">
                    {groupKey in messages
                      ? intl.formatMessage(messages[groupKey], {
                          count: groupedProjects[groupKey].length,
                        })
                      : groupname}
                  </h2>

                  {groupedProjects[groupKey]
                    .map((item, index) => {
                      let projectname = item.title;
                      let projecturl = item['@id'];
                      let extension = '';
                      if (item?.['@type'] === 'saw.persons.workgroup') {
                        const project = item?.getProjectList
                          ? item.getProjectList[0]
                          : null;
                        projectname = project?.title;
                        projecturl = project?.url ? project.url : item['@id'];
                        extension = ' (' + item.title + ')';
                      }
                      return { projectname, projecturl, extension };
                    })
                    .sort((a, b) =>
                      a.projectname.localeCompare(b.projectname, intl.locale),
                    )
                    .map(({ projectname, projecturl, extension }, index) => {
                      return (
                        <div className="label" key={index}>
                          <ConditionalLink
                            to={flattenToAppURL(projecturl)}
                            condition={!isEditMode}
                          >
                            {projectname}
                          </ConditionalLink>
                          {extension}
                        </div>
                      );
                    })}
                </div>
              );
            })}
        </div>
      )}

      {(hasBio || showBioHeadline) && (
        <div className="person-biography">
          <h2>
            <FormattedMessage id="persons.biography" />
          </h2>

          {/* Biography / Miscellaneous */}

          {(personData?.dob || personData?.dob_alternative) &&
            (!isPropertyHidden('dob') || canModify) && (
              <div
                className={
                  isPropertyHidden('dob')
                    ? 'person-dob state-private'
                    : 'person-dob '
                }
              >
                <span className="person-detail-label">
                  {intl.formatMessage(messages['persons.dob'])}
                </span>
                {': '}
                <span>
                  {[
                    personData?.dob && toLocalizedDate(personData?.dob),
                    personData?.dob_alternative,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </div>
            )}

          {personData?.pob && (!isPropertyHidden('pob') || canModify) && (
            <div
              id="person-pob"
              className={
                isPropertyHidden('pob')
                  ? 'person-pob state-private'
                  : 'person-pob '
              }
            >
              <span className="person-detail-label">
                {intl.formatMessage(messages['persons.pob'])}
              </span>
              {': '}
              <span>{personData?.pob}</span>
            </div>
          )}

          {(personData?.dod || personData?.dod_alternative) &&
            (!isPropertyHidden('dod') || canModify) && (
              <div
                id="person-dod"
                className={
                  isPropertyHidden('dod')
                    ? 'person-dod state-private'
                    : 'person-dod '
                }
              >
                <span className="person-detail-label">
                  {intl.formatMessage(messages['persons.dod'])}
                </span>
                {': '}
                <span>
                  {[
                    personData?.dod &&
                      !hidden.includes('dod') &&
                      toLocalizedDate(personData?.dod),
                    personData?.dod_alternative,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </div>
            )}

          {personData?.pod &&
            (((!hide_details_on_alumni || !alumni) &&
              !hidden.includes('pod')) ||
              canModify) && (
              <div
                id="person-pod"
                className={
                  (hide_details_on_alumni && alumni) || hidden.includes('pod')
                    ? 'state-private'
                    : ''
                }
              >
                <span className="person-detail-label">
                  {intl.formatMessage(messages['persons.pod'])}
                </span>
                {': '}
                <span>{personData?.pod}</span>
              </div>
            )}

          {personData?.obit && (
            <div className="person-obit">
              <span className="person-detail-label">
                {intl.formatMessage(messages['persons.obit'])}
              </span>
              {': '}
              <span>{personData?.obit}</span>
            </div>
          )}
          {personData?.research && (
            <div className="person-research">
              <span className="person-detail-label">
                {intl.formatMessage(messages['persons.research'])}
              </span>
              {': '}
              <span>{personData?.research}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PersonMetadata;
